<template>
  <div class="card-reset" :class="id">
    <div v-html="getCardStyles()"></div>
    <div ref="cardContainer" class="card-container"></div>
  </div>
</template>

<script lang="ts" setup>
import { Card, CardSubmitEvent } from '.';
import { renderElements, getStyles } from '@netcall/card-renderer';
import { nanoid } from 'nanoid';
import { onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';

const props = defineProps({
  cards: {
    type: Array as PropType<Card[]>,
    required: true,
  },
  colour: {
    type: String,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  location: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'submit', cardEvent: CardSubmitEvent): void;
}>();

const id: string = `nc_${nanoid(7)}`;
const cardContainer = ref<HTMLElement | null>(null);

watch(
  () => props.cards,
  (cards: Card[]) => renderCards(cards),
);

onMounted(() => {
  renderCards(props.cards);
  document.addEventListener('fresco-panel-submit', cardSubmitHandler);
});

onBeforeUnmount(() => {
  document.removeEventListener('fresco-panel-submit', cardSubmitHandler);
});

function getCardStyles() {
  return getStyles(props.colour, id).innerHTML;
}

function renderCards(cards: Card[]) {
  if (!cardContainer.value) {
    return;
  }

  cardContainer.value.innerHTML = '';
  cards.forEach((card) => renderCard(card));
}

function renderCard(card: Card) {
  if (isCardComponentsValid(card)) {
    const cardJson = JSON.stringify(card.content.components);
    renderElements(card.id || '', cardJson, cardContainer.value, props.location, props.readonly);
  } else {
    renderError(card);
  }
}

function renderError(card: Card) {
  renderElements(card.id || '', '{}', cardContainer.value, props.location, props.readonly);
}

function isCardComponentsValid(card: Card) {
  return card.content && card.content && card.content.components && card.content.components.length > 0;
}

function cardSubmitHandler(e: Event) {
  const customEvent = e as CustomEvent;
  onSubmit(customEvent.detail);
}

function onSubmit(e: CardSubmitEvent) {
  emit('submit', e);
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
// style resets
.card-reset {
  // font-family: "Open Sans", Helvetica, sans-serif;
  // font-size: 0.8rem;

  // font family conflicts
  // Bulma: Quicksand (actual)
  // RenderEngine: Arial (desired)
  // WebAssistantPreview: Open Sans
  // input, textarea, select, button {
  // 	font-family: Arial;
  // }

  .label {
    font-size: unset;
  }

  input,
  textarea {
    font-size: unset;
    height: unset;
    box-shadow: unset;
  }

  ul {
    list-style: unset;
  }

  ul,
  ol {
    padding-left: 40px;
    margin-block-start: 1em;
    margin-block-end: 1em;
  }

  a {
    color: #0000ee;
    text-decoration: underline;
  }

  .image {
    // fixes spacing issue for elements after the image
    display: unset;
    position: unset;
  }

  strong {
    color: unset;
  }
}
</style>
